import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/constants";
import {
  RECON_STATUS_STYLES, READINESS_STYLES, CONTROL_STATUS_STYLES, CONTROL_STATUSES,
  DIMENSIONS, DIM_VALUES, DIM_STYLES, RESPONSIBLE_PARTIES, PARTY_STYLES, PRIORITIES,
  PRIORITY_STYLES, PROCESSING_STAGES, AI_NOTICE, EVIDENCE_TYPES,
} from "@/lib/reconConstants";
import { ClipboardCheck, Loader2, RefreshCw, CheckCircle2, Lock, Unlock, AlertTriangle, Search, ArrowLeft, ListPlus, FolderPlus, Sparkles } from "lucide-react";
import { ExpectedEvidencePanel } from "./ExpectedEvidencePanel";

export default function ReconWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [controls, setControls] = useState([]);
  const [me, setMe] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ family: "all", status: "all", party: "all", flag: "all", q: "" });
  const [confirm, setConfirm] = useState(null); // {action, title, desc}
  const [finalize, setFinalize] = useState(null); // {issues, loading}

  const loadRecon = useCallback(() => api.get(`/recon/reconciliations/${id}`).then((res) => setR(res.data)).catch(() => {}), [id]);
  const loadControls = useCallback(() => api.get(`/recon/reconciliations/${id}/controls`).then((res) => setControls(res.data)).catch(() => {}), [id]);
  const loadCoverage = useCallback(() => api.get(`/blueprints/coverage/${id}`).then((res) => setCoverage(res.data)).catch(() => setCoverage(null)), [id]);

  useEffect(() => { loadRecon(); loadControls(); loadCoverage(); api.get("/recon/me").then((res) => setMe(res.data)).catch(() => {}); }, [loadRecon, loadControls, loadCoverage]);

  // Poll while processing
  useEffect(() => {
    if (r?.status !== "Processing") return;
    const t = setInterval(async () => {
      const res = await api.get(`/recon/reconciliations/${id}/progress`);
      setR((prev) => ({ ...prev, ...res.data }));
      if (res.data.status !== "Processing") { loadRecon(); loadControls(); clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [r?.status, id, loadRecon, loadControls]);

  if (!r) return <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>;
  const caps = me?.caps || {};

  // ---- Processing view ----
  if (r.status === "Processing" || (r.status === "Not Started" && r.progress > 0)) {
    const activeIdx = PROCESSING_STAGES.findIndex((s) => (r.progress_stage || "").toLowerCase().includes(s.toLowerCase().split(" ")[0]));
    return (
      <div>
        <Header r={r} onBack={() => navigate("/operations/reconciliation")} />
        <div className="p-6 md:p-8 max-w-2xl mx-auto space-y-6" data-testid="processing-view">
          <div className="rounded-md border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-3"><Loader2 className="h-5 w-5 animate-spin text-gold-dark" /><h2 className="font-display font-bold text-navy">Analyzing documents…</h2></div>
            <Progress value={r.progress || 0} />
            <p className="text-sm text-muted-foreground">{r.progress_stage || "Preparing…"}</p>
            <ol className="space-y-1.5 mt-2">
              {PROCESSING_STAGES.map((s, i) => (
                <li key={s} className={`text-xs flex items-center gap-2 ${i <= activeIdx ? "text-navy" : "text-muted-foreground"}`}>
                  {i < activeIdx ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : i === activeIdx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="h-3.5 w-3.5 rounded-full border border-slate-300 inline-block" />}
                  {s}
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-md border-l-4 border-l-gold bg-gold/10 p-3 text-sm text-navy flex gap-2"><AlertTriangle className="h-4 w-4 text-gold-dark shrink-0 mt-0.5" /> {AI_NOTICE}</div>
          {(r.progress_stage || "").startsWith("Processing failed") && (
            <div className="rounded-md border-l-4 border-l-red-400 bg-red-50 p-4 space-y-2">
              <p className="text-sm text-red-800">{r.progress_stage}</p>
              {caps.upload && <Button size="sm" variant="outline" data-testid="retry-analyze" onClick={async () => { await api.post(`/recon/reconciliations/${id}/analyze`); loadRecon(); }}>Retry</Button>}
            </div>
          )}
        </div>
      </div>
    );
  }

  const scores = r.scores || {};
  const families = [...new Set(controls.map((c) => c.family_id))].sort((a, b) => parseFloat(a.split(".")[1]) - parseFloat(b.split(".")[1]));
  const filtered = controls.filter((c) => {
    if (filters.family !== "all" && c.family_id !== filters.family) return false;
    if (filters.status !== "all" && (c.approved_status || c.ai_status) !== filters.status) return false;
    if (filters.party !== "all" && c.responsible_party !== filters.party) return false;
    if (filters.flag === "conflicts" && c.ai_status !== "Conflicting Information") return false;
    if (filters.flag === "missing_evidence" && !["Missing", "Not Confirmed"].includes(c.dimensions?.evidence)) return false;
    if (filters.flag === "manual" && !(c.needs_manual_review || c.ai_status === "Needs Manual Review")) return false;
    if (filters.flag === "open" && ["Confirmed Implemented", "Approved by ComplyX", "Not Applicable"].includes(c.ai_status)) return false;
    if (filters.q && !(`${c.control_id} ${c.requirement}`.toLowerCase().includes(filters.q.toLowerCase()))) return false;
    return true;
  });

  const evidenceWarning = scores.technical >= 70 && scores.evidence < 50;

  return (
    <div>
      <Header r={r} onBack={() => navigate("/operations/reconciliation")}
        actions={
          <div className="flex flex-wrap gap-2">
            {caps.upload && r.status !== "Completed" && (
              <Button size="sm" variant="outline" data-testid="reprocess-btn" onClick={() => setConfirm({ action: "reprocess", title: "Reprocess documents?", desc: "This re-runs AI analysis and replaces current AI-suggested findings. Your approved statuses on individual controls will be reset. Continue?" })} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Reprocess
              </Button>
            )}
            {caps.finalize && r.status !== "Completed" && (
              <Button size="sm" data-testid="finalize-btn" onClick={async () => {
                setFinalize({ loading: true });
                try { const res = await api.get(`/recon/reconciliations/${id}/finalize-check`); setFinalize({ issues: res.data.issues, justification: "" }); }
                catch { setFinalize(null); toast.error("Failed to run finalize check"); }
              }} className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                <Lock className="h-4 w-4 mr-1.5" /> Finalize Review
              </Button>
            )}
            {caps.finalize && r.status === "Completed" && (
              <Button size="sm" variant="outline" data-testid="reopen-btn" onClick={() => setConfirm({ action: "reopen", title: "Reopen reconciliation?", desc: "This reopens the reconciliation for further edits. History is preserved." })} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                <Unlock className="h-4 w-4 mr-1.5" /> Reopen Review
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6 md:p-8 space-y-6">
        {/* Readiness summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="readiness-cards">
          <ScoreCard label="Technical" value={scores.technical} pct />
          <ScoreCard label="Documentation" value={scores.documentation} pct />
          <ScoreCard label="Evidence" value={scores.evidence} pct />
          {r.ssp_source_status === "No Existing SSP"
            ? <ScoreCard label="SSP Accuracy" value="No SSP" note="Not Yet Evaluated" />
            : <ScoreCard label="SSP Accuracy" value={scores.ssp_accuracy} pct />}
          <ScoreCard label="Client Actions" value={scores.client_actions} />
          <ScoreCard label="Implementer Actions" value={scores.implementer_actions} />
          <ScoreCard label="ComplyX Actions" value={scores.complyx_actions} />
          <div className="rounded-md border border-border bg-card p-4" data-testid="overall-readiness">
            <div className="text-xs text-muted-foreground mb-1">Overall Readiness</div>
            <span className={`text-[11px] px-2 py-1 rounded border font-semibold ${READINESS_STYLES[r.readiness] || ""}`}>{r.readiness}</span>
          </div>
        </div>

        {evidenceWarning && (
          <div className="rounded-md border-l-4 border-l-amber-400 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2" data-testid="evidence-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Technical implementation appears substantially complete, but evidence coverage is insufficient to support assessment readiness.
          </div>
        )}

        {coverage && coverage.totals.applicable > 0 && (
          <div className="space-y-3" data-testid="evidence-coverage-section">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div className="rounded-md border border-gold/40 bg-gold/10 p-4"><div className="text-xs text-muted-foreground mb-1">Evidence Coverage</div><div className="text-2xl font-bold text-navy">{coverage.overall_pct}%</div></div>
              <ScoreCard label="Fully Supported" value={coverage.controls_fully_supported} />
              <ScoreCard label="Partially Supported" value={coverage.controls_partially_supported} />
              <ScoreCard label="Missing Evidence" value={coverage.controls_missing_evidence} />
              <ScoreCard label="Implementer Gaps" value={coverage.implementer_gaps} />
              <ScoreCard label="Client Gaps" value={coverage.client_gaps} />
              <ScoreCard label="ComplyX Gaps" value={coverage.complyx_gaps} />
            </div>
            {coverage.warnings.length > 0 && (
              <div className="rounded-md border-l-4 border-l-amber-400 bg-amber-50 p-4 text-sm text-amber-900" data-testid="coverage-warnings">
                <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle className="h-4 w-4" /> {coverage.warnings.length} control{coverage.warnings.length > 1 ? "s" : ""}: technical implementation appears complete, but required evidence remains incomplete.</div>
                <div className="text-xs">{coverage.warnings.slice(0, 12).map((w) => w.control_id).join(", ")}{coverage.warnings.length > 12 ? "…" : ""}</div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="rounded-md border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <Label className="text-xs">Search controls</Label>
            <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input data-testid="control-search" className="pl-8" placeholder="Control # or requirement keyword" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} /></div>
          </div>
          <FilterSelect label="Family" testid="filter-family" value={filters.family} onChange={(v) => setFilters((f) => ({ ...f, family: v }))} options={[["all", "All families"], ...families.map((f) => [f, `${f} ${controls.find((c) => c.family_id === f)?.family_name || ""}`])]} />
          <FilterSelect label="Status" testid="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={[["all", "All statuses"], ...CONTROL_STATUSES.map((s) => [s, s])]} />
          <FilterSelect label="Responsible" testid="filter-party" value={filters.party} onChange={(v) => setFilters((f) => ({ ...f, party: v }))} options={[["all", "All parties"], ...RESPONSIBLE_PARTIES.map((s) => [s, s])]} />
          <FilterSelect label="Flags" testid="filter-flag" value={filters.flag} onChange={(v) => setFilters((f) => ({ ...f, flag: v }))} options={[["all", "All"], ["conflicts", "Conflicts only"], ["missing_evidence", "Missing evidence"], ["manual", "Manual review"], ["open", "Open actions"]]} />
        </div>

        {/* Control table */}
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-xs text-muted-foreground">{filtered.length} of {controls.length} controls</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-left">
                  {["Control", "Requirement", "SSP", "Report", "Evidence", "Reconciliation Status", "Responsible", "Priority", "Reviewer"].map((h) => (
                    <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`control-row-${c.control_id}`} onClick={() => setSelected(c)} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-3 font-mono text-xs font-medium text-navy whitespace-nowrap">{c.control_id}</td>
                    <td className="px-3 py-3 text-xs max-w-[280px]"><span className="line-clamp-2">{c.requirement}</span></td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">{c.ssp_status}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">{c.report_confirmation}</td>
                    <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded ${DIM_STYLES[c.dimensions?.evidence] || ""}`}>{c.dimensions?.evidence || "—"}</span></td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${CONTROL_STATUS_STYLES[c.approved_status || c.ai_status] || ""}`}>{c.approved_status || c.ai_status}</span>
                      {!c.approved_status && <span className="ml-1 text-[10px] text-violet-700">AI</span>}
                    </td>
                    <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded ${PARTY_STYLES[c.responsible_party] || ""}`}>{c.responsible_party}</span></td>
                    <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded border ${PRIORITY_STYLES[c.priority] || ""}`}>{c.priority}</span></td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{c.reviewer ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ControlPanel control={selected} onClose={() => setSelected(null)} caps={caps} locked={r.status === "Completed"} reconId={id}
        onSaved={() => { loadControls(); loadRecon(); }} />

      <FinalizeDialog state={finalize} onClose={() => setFinalize(null)} reconId={id}
        onDone={() => { setFinalize(null); loadRecon(); loadControls(); }} readiness={r.readiness} />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>{confirm?.title}</AlertDialogTitle><AlertDialogDescription>{confirm?.desc}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ok" onClick={async () => {
              try {
                if (confirm.action === "reprocess") { await api.post(`/recon/reconciliations/${id}/analyze`); toast.success("Reprocessing started"); }
                if (confirm.action === "finalize") { await api.post(`/recon/reconciliations/${id}/finalize`); toast.success("Reconciliation finalized"); }
                if (confirm.action === "reopen") { await api.post(`/recon/reconciliations/${id}/reopen`); toast.success("Reconciliation reopened"); }
                loadRecon(); loadControls();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              setConfirm(null);
            }}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Header({ r, actions, onBack }) {
  return (
    <header className="bg-navy px-6 md:px-8 py-6">
      <button onClick={onBack} data-testid="workspace-back" className="text-slate-300 hover:text-white text-xs flex items-center gap-1 mb-2"><ArrowLeft className="h-3.5 w-3.5" /> Back to reconciliations</button>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="overline text-gold mb-1.5">Operations · Reconciliation Workspace</p>
          <h1 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-gold" aria-hidden="true" /> {r.customer_name}
          </h1>
          <p className="text-slate-300 text-xs mt-2">{r.framework_name} · SSP {r.ssp_version || "—"} · Report {r.report_version || "—"} · {r.implementation_partner_name || "No partner"} · Reviewer {r.assigned_reviewer}</p>
          <p className="text-slate-400 text-[11px] mt-1">Created {fmtDate(r.created_at)} · Last processed {r.last_processed_at ? fmtDate(r.last_processed_at) : "—"}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[11px] px-2 py-1 rounded border ${RECON_STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
          {actions}
        </div>
      </div>
    </header>
  );
}

function ScoreCard({ label, value, pct, note }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className={`font-display font-bold text-navy ${note ? "text-base" : "text-2xl"}`}>{value != null ? (pct ? `${value}%` : value) : "—"}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}{note ? ` · ${note}` : ""}</div>
    </div>
  );
}

function FilterSelect({ label, testid, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[170px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function ControlPanel({ control, onClose, caps, locked, reconId, onSaved }) {
  const [cState, setC] = useState(null);
  const [showAction, setShowAction] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  useEffect(() => { setC(control); setShowAction(false); setShowEvidence(false); }, [control]);
  if (!control) return null;
  const c = cState && cState.id === control.id ? cState : control;
  const editable = !locked;

  const patch = async (body, msg) => {
    try { const res = await api.put(`/recon/controls/${control.id}`, body); setC(res.data); onSaved(); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const decideFinding = async (fid, decision) => {
    try { const res = await api.put(`/recon/controls/${control.id}/findings/${fid}`, { decision }); setC(res.data); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Sheet open={!!control} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="control-panel">
        <SheetHeader>
          <SheetTitle className="font-display text-navy flex items-center gap-2">
            <span className="font-mono">{c.control_id}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded border ${CONTROL_STATUS_STYLES[c.approved_status || c.ai_status] || ""}`}>{c.approved_status || c.ai_status}</span>
          </SheetTitle>
          <SheetDescription>{c.family_name} · {c.framework === "cmmc_l2" ? "CMMC Level 2" : "NIST 800-171 R2"}</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-4 text-sm">
          {/* Convert to action / evidence */}
          {editable && caps.assign && (
            <div className="flex flex-wrap gap-2" data-testid="control-convert">
              <Button size="sm" variant="outline" data-testid="control-create-action" onClick={() => { setShowAction((v) => !v); setShowEvidence(false); }}><ListPlus className="h-4 w-4 mr-1.5" /> Create Action</Button>
              <Button size="sm" variant="outline" data-testid="control-request-evidence" onClick={() => { setShowEvidence((v) => !v); setShowAction(false); }}><FolderPlus className="h-4 w-4 mr-1.5" /> Request Evidence</Button>
            </div>
          )}
          {showAction && <CreateActionForm reconId={reconId} control={c} onDone={() => { setShowAction(false); toast.success("Action created"); }} />}
          {showEvidence && <CreateEvidenceForm reconId={reconId} control={c} onDone={() => { setShowEvidence(false); toast.success("Evidence request created"); }} />}

          <ExpectedEvidencePanel reconId={reconId} control={c} caps={caps} />

          {/* Requirement */}
          <Section title="Control Requirement">
            <p className="text-navy leading-relaxed">{c.requirement}</p>
          </Section>

          {/* SSP statement */}
          <Section title={c.ssp_status === "No Existing SSP" ? "SSP Source" : "SSP Implementation Statement"}>
            {c.ssp_status === "No Existing SSP" ? (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900" data-testid="control-no-ssp-notice">
                <div className="font-semibold mb-1">No Existing SSP</div>
                This customer does not yet have an SSP. Reconciliation findings may be used later by the SSP Builder to create the initial SSP.
              </div>
            ) : (
              <>
                <MetaRow label="Claimed status" value={c.ssp_status} />
                <MetaRow label="SSP page" value={c.ssp_page ? `p.${c.ssp_page}` : "not cited"} />
                <blockquote className="border-l-2 border-navy/20 pl-3 text-muted-foreground italic mt-2">{c.ssp_statement || "No SSP statement extracted."}</blockquote>
              </>
            )}
          </Section>

          {/* Report findings */}
          <Section title="Implementation Report Findings">
            {(c.report_findings || []).length === 0 ? <p className="text-muted-foreground text-xs">No report findings extracted for this control.</p> :
              (c.report_findings || []).map((f) => (
                <div key={f.id} className="rounded border border-border p-3 mb-2" data-testid={`finding-${f.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-muted-foreground">{f.page ? `Report p.${f.page}` : "no page"} · Confidence {f.confidence}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${f.decision === "accepted" ? "bg-emerald-100 text-emerald-800" : f.decision === "rejected" ? "bg-red-100 text-red-800" : f.decision === "irrelevant" ? "bg-slate-100 text-slate-600" : "bg-violet-100 text-violet-800"}`}>{f.decision === "pending" ? "AI Suggested" : f.decision}</span>
                  </div>
                  <p className="text-navy text-xs">{f.text}</p>
                  {f.interpretation && <p className="text-muted-foreground text-xs mt-1 italic">AI: {f.interpretation}</p>}
                  {(f.technologies || []).length > 0 && <div className="mt-1 flex flex-wrap gap-1">{f.technologies.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-navy/10 text-navy">{t}</span>)}</div>}
                  {editable && caps.approve_finding && (
                    <div className="flex gap-1.5 mt-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200" data-testid={`finding-accept-${f.id}`} onClick={() => decideFinding(f.id, "accepted")}>Accept</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" data-testid={`finding-reject-${f.id}`} onClick={() => decideFinding(f.id, "rejected")}>Reject</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => decideFinding(f.id, "irrelevant")}>Irrelevant</Button>
                    </div>
                  )}
                </div>
              ))}
          </Section>

          {/* Dimensions */}
          <Section title="Implementation Dimensions">
            <div className="grid grid-cols-1 gap-1.5">
              {DIMENSIONS.map((d) => (
                <div key={d.key} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-navy">{d.label}</span>
                  {editable ? (
                    <Select value={c.dimensions?.[d.key]} onValueChange={(v) => patch({ dimensions: { [d.key]: v } })}>
                      <SelectTrigger className="w-[190px] h-8" data-testid={`dim-${d.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{DIM_VALUES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className={`text-[11px] px-2 py-0.5 rounded ${DIM_STYLES[c.dimensions?.[d.key]] || ""}`}>{c.dimensions?.[d.key]}</span>}
                </div>
              ))}
            </div>
          </Section>

          {/* Recommended SSP Update */}
          <SSPRecommendation control={c} setC={setC} caps={caps} editable={editable} />

          {/* Conclusion */}
          <Section title="Reconciliation Conclusion">
            <MetaRow label="AI recommended" value={c.ai_status} />
            <MetaRow label="AI confidence" value={c.confidence} />
            {c.conclusion && <p className="text-muted-foreground text-xs italic mt-1">{c.conclusion}</p>}
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="space-y-1.5"><Label className="text-xs">Responsible party</Label>
                <Select value={c.responsible_party} onValueChange={(v) => patch({ responsible_party: v })} disabled={!editable}>
                  <SelectTrigger data-testid="ctrl-party"><SelectValue /></SelectTrigger>
                  <SelectContent>{RESPONSIBLE_PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
                <Select value={c.priority} onValueChange={(v) => patch({ priority: v })} disabled={!editable}>
                  <SelectTrigger data-testid="ctrl-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5 mt-3"><Label className="text-xs">Internal notes</Label>
              <Textarea data-testid="ctrl-internal-notes" defaultValue={c.internal_notes} rows={2} disabled={!editable} onBlur={(e) => patch({ internal_notes: e.target.value })} /></div>
            <div className="space-y-1.5 mt-2"><Label className="text-xs">Customer-facing notes</Label>
              <Textarea data-testid="ctrl-customer-notes" defaultValue={c.customer_notes} rows={2} disabled={!editable} onBlur={(e) => patch({ customer_notes: e.target.value })} /></div>

            {/* Approve */}
            <div className="mt-4 rounded-md bg-slate-50 border border-border p-3">
              <Label className="text-xs mb-2 block">Admin Approved Status <span className="text-muted-foreground">(AI cannot approve — ComplyX must set this)</span></Label>
              {editable && caps.approve_finding ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={c.approved_status || ""} onValueChange={(v) => patch({ approved_status: v }, "Status approved")}>
                    <SelectTrigger className="w-[240px]" data-testid="ctrl-approved-status"><SelectValue placeholder="Set approved status…" /></SelectTrigger>
                    <SelectContent>{CONTROL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ctrl-approve" onClick={() => patch({ approved_status: "Approved by ComplyX" }, "Control approved by ComplyX")}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                </div>
              ) : <span className={`text-[11px] px-2 py-0.5 rounded border ${CONTROL_STATUS_STYLES[c.approved_status] || "bg-slate-100 text-slate-600"}`}>{c.approved_status || "Not yet approved"}</span>}
              {c.reviewer && <p className="text-[11px] text-muted-foreground mt-2">Reviewed by {c.reviewer} · {c.review_date ? fmtDate(c.review_date) : ""}</p>}
            </div>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-border pt-4 first:border-0 first:pt-0">
      <h3 className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

const REC_TYPE_STYLES = {
  "No Update Recommended": "bg-slate-100 text-slate-600",
  "Update Recommended": "bg-blue-100 text-blue-800",
  "Correction Recommended": "bg-amber-100 text-amber-800",
  "Needs Manual Review": "bg-violet-100 text-violet-800",
};

function SSPRecommendation({ control, setC, caps, editable }) {
  const c = control;
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const rec = c.ssp_recommendation;
  const approved = c.approved_ssp_recommendation;
  const canGen = editable && caps.review;

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/recon/controls/${c.id}/ssp-recommendation`); setC(r.data); toast.success("SSP recommendation generated."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  const decide = async (action, edited_statement) => {
    setBusy(true);
    try { const r = await api.post(`/recon/controls/${c.id}/ssp-recommendation/decision`, { action, edited_statement: edited_statement || "" }); setC(r.data); setEdit(false); toast.success("Saved (current SSP unchanged)."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Section title="Recommended SSP Update">
      <div className="space-y-3" data-testid="ssp-recommendation-section">
        <div>
          <div className="text-[11px] font-semibold text-navy mb-1">Current SSP Statement</div>
          <blockquote className="border-l-2 border-navy/20 pl-3 text-muted-foreground italic text-xs" data-testid="ssp-rec-current">
            {c.ssp_status === "No Existing SSP" ? "No existing SSP for this customer." : (c.ssp_statement || "No SSP statement extracted.")}
          </blockquote>
        </div>

        {!rec ? (
          <div className="rounded-md border border-border bg-slate-50 p-3 text-xs">
            <p className="text-muted-foreground mb-2">Generate an AI recommendation that converts the reviewed reconciliation state into audit-ready SSP language. The current SSP is never changed automatically.</p>
            <Button size="sm" variant="outline" disabled={!canGen || busy} onClick={generate} data-testid="ssp-rec-generate">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate Recommendation
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${REC_TYPE_STYLES[rec.recommendation_type] || "bg-slate-100"}`} data-testid="ssp-rec-type">{rec.recommendation_type}</span>
              <span className="text-[10px] text-muted-foreground">Governing status: {rec.governing_status}</span>
              {canGen && <Button size="sm" variant="ghost" className="h-6 text-[11px] ml-auto" disabled={busy} onClick={generate} data-testid="ssp-rec-regenerate">Regenerate</Button>}
            </div>
            <div>
              <div className="text-[11px] font-semibold text-navy mb-1">AI Recommended SSP Statement</div>
              {edit ? (
                <Textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} className="text-xs" data-testid="ssp-rec-edit" />
              ) : (
                <p className="text-navy text-xs leading-relaxed" data-testid="ssp-rec-statement">{rec.recommended_statement}</p>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground"><b>Reason:</b> {rec.reason}</div>
            <div className="flex flex-wrap gap-1" data-testid="ssp-rec-basis">
              {(rec.source_basis || []).map((b) => <span key={b} className="text-[10px] px-1.5 py-0.5 rounded bg-navy/10 text-navy">{b}</span>)}
            </div>
            {approved && (
              <div className="text-[11px] rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-emerald-800" data-testid="ssp-rec-approved">
                {approved.status === "Kept Current" ? "No SSP update approved (kept current)." : `Approved reconciliation recommendation saved (${approved.status}) — current SSP unchanged.`}
              </div>
            )}
            {editable && caps.review && (
              <div className="flex flex-wrap gap-2 pt-1">
                {!edit ? (
                  <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" disabled={busy} onClick={() => decide("accept")} data-testid="ssp-rec-accept">Accept Recommendation</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => { setDraft(rec.recommended_statement); setEdit(true); }} data-testid="ssp-rec-edit-btn">Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => decide("keep")} data-testid="ssp-rec-keep">Keep Current SSP</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" className="bg-navy text-white h-7 text-xs" disabled={busy} onClick={() => decide("edit", draft)} data-testid="ssp-rec-save-edit">Save Edited Recommendation</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEdit(false)}>Cancel</Button>
                  </>
                )}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">This does not modify the current SSP or the Admin Approved Status. It is saved as a recommendation for the SSP Builder.</p>
          </div>
        )}
      </div>
    </Section>
  );
}

function MetaRow({ label, value }) {
  return <div className="flex justify-between gap-3 py-0.5"><span className="text-muted-foreground text-xs">{label}</span><span className="text-navy text-xs text-right">{value || "—"}</span></div>;
}

function CreateActionForm({ reconId, control, onDone }) {
  const [f, setF] = useState({ title: `Remediate ${control.control_id}`, responsible_org: control.responsible_party || "ComplyX", priority: control.priority || "Medium", due_date: "", description: control.conclusion || "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await api.post("/recon/actions", { reconciliation_id: reconId, control_id: control.control_id, control_doc_id: control.id, created_from: "control_gap", ...f });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border border-gold/40 bg-gold/5 p-3 space-y-2" data-testid="create-action-form">
      <div className="text-xs font-semibold text-navy">New Action Item</div>
      <Input data-testid="action-form-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Action title" />
      <div className="grid grid-cols-3 gap-2">
        <Select value={f.responsible_org} onValueChange={(v) => setF({ ...f, responsible_org: v })}><SelectTrigger data-testid="action-form-org"><SelectValue /></SelectTrigger><SelectContent>{RESPONSIBLE_PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="action-form-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Input type="date" data-testid="action-form-due" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
      </div>
      <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" />
      <Button size="sm" data-testid="action-form-submit" disabled={saving} onClick={submit} className="bg-navy text-white hover:bg-navy-700">{saving ? "Saving…" : "Create Action"}</Button>
    </div>
  );
}

function CreateEvidenceForm({ reconId, control, onDone }) {
  const [f, setF] = useState({ title: `Evidence for ${control.control_id}`, evidence_type: "Configuration export", requested_from: control.responsible_party || "Client", due_date: "", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await api.post("/recon/evidence", { reconciliation_id: reconId, control_id: control.control_id, control_doc_id: control.id, ...f });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-2" data-testid="create-evidence-form">
      <div className="text-xs font-semibold text-navy">New Evidence Request</div>
      <Input data-testid="evidence-form-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Request title" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.evidence_type} onValueChange={(v) => setF({ ...f, evidence_type: v })}><SelectTrigger data-testid="evidence-form-type"><SelectValue /></SelectTrigger><SelectContent>{EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Select value={f.requested_from} onValueChange={(v) => setF({ ...f, requested_from: v })}><SelectTrigger data-testid="evidence-form-from"><SelectValue /></SelectTrigger><SelectContent>{RESPONSIBLE_PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
      </div>
      <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What is needed?" />
      <Button size="sm" data-testid="evidence-form-submit" disabled={saving} onClick={submit} className="bg-navy text-white hover:bg-navy-700">{saving ? "Saving…" : "Request Evidence"}</Button>
    </div>
  );
}

function FinalizeDialog({ state, onClose, reconId, onDone, readiness }) {
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setJustification(""); }, [state]);
  if (!state) return null;
  const issues = state.issues || [];
  const clean = issues.length === 0;

  const doFinalize = async () => {
    if (!clean && justification.trim().length < 10) { toast.error("A justification (min 10 characters) is required to override."); return; }
    setSubmitting(true);
    try {
      await api.post(`/recon/reconciliations/${reconId}/finalize`, { override: !clean, justification });
      toast.success(clean ? "Reconciliation finalized" : "Finalized with documented override");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="finalize-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2"><Lock className="h-5 w-5 text-gold-dark" /> Finalize Reconciliation</DialogTitle>
          <DialogDescription>Finalizing snapshots the current results into version history and marks the reconciliation Completed. Overall readiness: {readiness}.</DialogDescription>
        </DialogHeader>
        {state.loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>
        ) : clean ? (
          <div className="rounded-md border-l-4 border-l-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900 flex gap-2" data-testid="finalize-clean">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> All guardrail checks passed. This reconciliation is ready to finalize.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border-l-4 border-l-amber-400 bg-amber-50 p-3" data-testid="finalize-issues">
              <p className="text-sm text-amber-900 font-medium flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" /> {issues.length} unresolved issue{issues.length > 1 ? "s" : ""} remain:</p>
              <ul className="space-y-1 text-xs text-amber-900">
                {issues.map((i) => (
                  <li key={i.type} data-testid={`finalize-issue-${i.type}`}>• <strong>{i.count}</strong> — {i.label}{i.items?.length ? <span className="text-amber-700"> ({i.items.slice(0, 6).join(", ")}{i.items.length > 6 ? "…" : ""})</span> : null}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Override justification (required, min 10 chars) *</Label>
              <Textarea rows={3} data-testid="finalize-justification" value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Explain why this reconciliation is being finalized despite the unresolved issues above. This is recorded in the audit log." />
            </div>
            <p className="text-[11px] text-muted-foreground">The override, your justification, user, date, and unresolved issues will be recorded in the audit log.</p>
          </div>
        )}
        {!state.loading && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="finalize-cancel">Cancel</Button>
            <Button onClick={doFinalize} disabled={submitting} data-testid="finalize-confirm" className={clean ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-gold text-navy hover:bg-gold-dark font-semibold"}>
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Finalizing…</> : clean ? "Finalize" : "Override & Finalize"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
